import { Variant, Product } from "@/types/types";
import { getMainVariant } from "@/utils/product/getMainVariant";
import { removeHtml } from "../../../utils/string/removeHtml";

export type StructuredDataProductGroup = {
  "@type": "ProductGroup";
  "@context": "https://schema.org/";
  name: string;
  url: string;
  productGroupID: string;
  description?: string;

  brand?: {
    "@type": "Brand";
    name: string;
  };
  model?: string;
  category?: string;
  audience?: {
    "@type": "PeopleAudience";
    suggestedGender?: string;
  };
  material?: string[];
  size?: string[];

  variesBy?: string[];
  hasVariant?: StructuredDataProduct[];
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
};

export type StructuredDataProduct = {
  "@type": "Product";
  "@context"?: "https://schema.org/";
  name: string;
  url: string;
  productId: string;
  image: string;
  description?: string;

  brand?: {
    "@type": "Brand";
    name: string;
  };
  model?: string;
  category?: string;
  audience?: {
    "@type": "PeopleAudience";
    suggestedGender?: string;
  };
  material?: string[];
  size?: string[];
  color?: string;
  offers?: StructuredDataOffer | StructuredDataOffer[];
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
  pattern?: string;
};

type StructuredDataOffer = {
  "@type": "Offer";
  sku?: string;
  price?: string;
  priceCurrency: string;
  availability?: string;
  url: string;
  priceSpecification?: {
    "@type": "UnitPriceSpecification";
    price: string;
    priceCurrency: string;
    priceType: string;
    valueAddedTaxIncluded: string;
  };
  availabilityStarts?: string;
  gtin13?: string;
  gtin8?: string;
  gtin12?: string;
};

export const buildStructuredData = (
  product: Product,
): StructuredDataProductGroup | StructuredDataProduct | object => {
  if (
    product.__typename === "CompositeBundle" ||
    !product.variants ||
    product.variants.length === 0
  ) {
    return {};
  }
  const { variants } = product;

  const colours: Set<string> = new Set(
    variants
      .map((variant: Variant) => variant.differentiators?.colour)
      .filter((c) => c !== undefined && c !== null),
  );

  // Build common data that's shared between Product and ProductGroup

  if (variants.length === 1 || colours.size <= 1) {
    // Single product or single color - return Product type
    const commonData = buildCommonData(product);
    const allOffers = variants.flatMap((variant) =>
      buildOfferTypeData(variant),
    );
    if (allOffers.length === 0 || allOffers[0] === undefined) {
      return {};
    }
    const firstOffer: StructuredDataOffer = allOffers[0];
    const mainVariant = getMainVariant(product);

    const data: StructuredDataProduct = {
      "@type": "Product",
      "@context": "https://schema.org/",
      ...commonData,
      name: product.title,
      url: `https://www.johnlewis.com${mainVariant.canonicalURL}`,
      productId: product.aliases.pimProductId,
      image: `https:${mainVariant.images.primary}`,
      offers: variants.length === 1 ? firstOffer : allOffers,
    };

    if (variants.length > 1) {
      const allSizes = getAllUniqueSizes(variants);
      if (allSizes.length > 0) {
        data.size = allSizes;
      }
    }

    // Add material from first variant if available
    if (mainVariant) {
      const materialValues = getMaterialValues(mainVariant);
      if (materialValues.length > 0) {
        data.material = materialValues;
      }
    }

    const patternAttribute = product.attributes?.find(
      (attr) => attr.key.toLowerCase() === "typeofpattern",
    );
    if (patternAttribute && patternAttribute.values.length > 0) {
      data.pattern = patternAttribute.values.join(", ");
    }

    return data;
  } else {
    // Multiple colors - return ProductGroup type
    const hasVariant = buildProductVariants(product, variants);
    const allSizes = getAllUniqueSizes(variants);
    const commonData = buildCommonData(product);
    const data: StructuredDataProductGroup = {
      "@type": "ProductGroup",
      "@context": "https://schema.org/",
      ...commonData,
      name: product.title,
      url: `https://www.johnlewis.com${product.canonicalURL}`,
      productGroupID: product.aliases.pimProductId,

      variesBy: ["color"],
      hasVariant,
    };
    //TODO:Sizes
    if (allSizes.length > 0) {
      data.size = allSizes;
    }
    return data;
  }
};

const stripBrandName = (name: string, brandName: string): string => {
  const regex = new RegExp(`\\b${brandName}\\b`, "i");
  return name.replace(regex, "").trim();
};

const buildCategory = (product: Product): string => {
  const path = product.browsePageHierarchy.slice(1);
  return path.map((p) => p.name).join(" > ");
};

const buildCommonData = (product: Product) => {
  const commonData: { [key: string]: any } = {
    description: removeHtml(product.description),

    brand: {
      "@type": "Brand",
      name: product.brand?.name,
    },
    model: stripBrandName(product.title, product.brand?.name || ""),
    category: buildCategory(product),
  };

  if (product.reviewSummary?.averageRating) {
    commonData.aggregateRating = {
      "@type": "AggregateRating",
      reviewCount: product.reviewSummary.numberOfReviews,
      ratingValue: product.reviewSummary.averageRating,
    };
  }
  return commonData;
};

const buildProductVariants = (
  product: Product,
  variants: Variant[],
): StructuredDataProduct[] => {
  // Group variants by color
  const variantsByColor = variants.reduce(
    (acc: Record<string, Variant[]>, variant) => {
      const color = variant.differentiators?.colour || "default";
      if (!acc[color]) {
        acc[color] = [];
      }
      acc[color].push(variant);
      return acc;
    },
    {},
  );

  // Create a Product for each color group
  return Object.entries(variantsByColor).map(([color, colorVariants]) => {
    const mainVariant = colorVariants[0];
    const canonicalURL = mainVariant?.canonicalURL || product.canonicalURL;
    const offers = colorVariants.map((variant) => buildOfferTypeData(variant));

    const productData: StructuredDataProduct = {
      "@type": "Product",
      name: `${product.title}, ${color}`,
      url: `https://www.johnlewis.com${canonicalURL}`,
      productId: product.aliases.pimProductId,
      image: `https:${mainVariant?.images.primary}`,

      color: color,
      offers: offers,
    };

    // Add material from main variant if available
    if (mainVariant) {
      const materialValues = getMaterialValues(mainVariant);
      if (materialValues.length > 0) {
        productData.material = materialValues;
      }
    }

    const patternAttribute = product.attributes?.find(
      (attr) => attr.key.toLowerCase() === "typeofpattern",
    );
    if (patternAttribute && patternAttribute.values.length > 0) {
      productData.pattern = patternAttribute.values.join(", ");
    }

    return productData;
  });
};
const buildAvailability = (
  variant: Variant,
): { availability: string; availabilityStarts?: string } => {
  if (
    variant.availability?.availableToOrder &&
    !variant.availability.preOrder
  ) {
    return { availability: "https://schema.org/InStock" };
  } else if (
    !variant.availability?.availableToOrder &&
    variant.availability?.unavailableReason == "inStoreOnly"
  ) {
    return { availability: "https://schema.org/InStoreOnly" };
  } else if (
    variant.availability?.preOrder &&
    variant.availability?.availableToOrder
  ) {
    return {
      availability: "https://schema.org/PreOrder",
      availabilityStarts:
        variant.availability?.preOrder.streetDate || undefined,
    };
  } else if (
    ["outOfStock", "notYetAvailable"].includes(
      variant.availability?.unavailableReason ?? "",
    )
  ) {
    return { availability: "https://schema.org/OutOfStock" };
  } else {
    return { availability: "https://schema.org/Discontinued" };
  }
};
const buildOfferTypeData = (variant: Variant): StructuredDataOffer => {
  const data: StructuredDataOffer = {
    "@type": "Offer",
    sku: variant.aliases.skuId,
    price: variant.price?.value,
    priceCurrency: "GBP",
    url: `https://www.johnlewis.com${variant.canonicalURL}`,
  };

  const { consumerBarcodes } = variant;

  const { availability, availabilityStarts } = buildAvailability(variant);
  data.availability = availability;
  if (availabilityStarts) {
    data.availabilityStarts = availabilityStarts;
  }

  if (consumerBarcodes != undefined) {
    if (consumerBarcodes?.ean13?.length > 0) {
      data.gtin13 = consumerBarcodes.ean13[0];
    }
    if (consumerBarcodes?.ean8?.length > 0) {
      data.gtin8 = consumerBarcodes.ean8[0];
    }
    if (consumerBarcodes?.upc12?.length > 0) {
      data.gtin12 = consumerBarcodes.upc12[0];
    }
  }

  return data;
};

const getAllUniqueSizes = (variants: Variant[]): string[] => {
  const sizes = new Set<string>();

  variants.forEach((variant) => {
    const size = variant.differentiators?.size;
    if (size) {
      sizes.add(size);
    }
  });

  return Array.from(sizes);
};

const getMaterialValues = (variant: Variant): string[] => {
  const materialAttribute = variant.attributes?.find(
    (attr) => attr.key.toLowerCase() === "material",
  );
  return materialAttribute?.values || [];
};

export default buildStructuredData;
